import { useCallback } from "react";
import type { FormShallowStoreHook } from "../../store";

export interface UseTouchedFieldsArgs {
  useFormShallowStore: FormShallowStoreHook;
}

export const useTouchedFields = ({ useFormShallowStore }: UseTouchedFieldsArgs) => {
  const {
    touchedFields,
    setTouchedFields,
    repeatInputs,
    setRepeatInputs,
    repeatTouchedFields,
    setRepeatTouchedFields,
  } = useFormShallowStore(
    "touchedFields",
    "setTouchedFields",
    "repeatInputs",
    "setRepeatInputs",
    "repeatTouchedFields",
    "setRepeatTouchedFields"
  );

  const setRepeatValue = useCallback(
    (fieldId: string, value: string) => {
      setRepeatInputs({ ...repeatInputs, [fieldId]: value });
    },
    [repeatInputs, setRepeatInputs]
  );

  const markFieldTouched = useCallback(
    (fieldId: string) => {
      if (touchedFields.has(fieldId)) return;
      setTouchedFields(new Set([...touchedFields, fieldId]));
    },
    [touchedFields, setTouchedFields]
  );

  const handleFieldBlur = markFieldTouched;

  const handleRepeatBlur = useCallback(
    (fieldId: string) => {
      if (!repeatTouchedFields.has(fieldId)) setRepeatTouchedFields(new Set([...repeatTouchedFields, fieldId]));
      markFieldTouched(fieldId);
    },
    [markFieldTouched, repeatTouchedFields, setRepeatTouchedFields]
  );

  /**
   * Walks back the record of a field having been answered, so no inline error shows until the enduser
   * returns to it. Used when a value is invalidated by something other than the enduser.
   */
  const untouchFields = useCallback(
    (fieldIds: ReadonlySet<string>) => {
      if (!fieldIds.size) return;
      setTouchedFields(new Set([...touchedFields].filter((id) => !fieldIds.has(id))));
    },
    [touchedFields, setTouchedFields]
  );

  return {
    touchedFields,
    setTouchedFields,
    repeatInputs,
    setRepeatValue,
    repeatTouchedFields,
    handleFieldBlur,
    handleRepeatBlur,
    markFieldTouched,
    untouchFields,
  };
};
